<template>
  <div class="rich-text-editor">
    <label v-if="label">{{ label }}</label>
    <vue-editor
      v-model="internalValue"
      :editor-toolbar="customToolbar"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInput"
    />
    <div v-if="error" class="error--text text-caption mt-1">
      {{ errorMessages }}
    </div>
  </div>
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor'

// Register custom font sizes
const Size = Quill.import('attributors/style/size')
Size.whitelist = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px', '42px', '54px']
Quill.register(Size, true)

// Register custom fonts
const Font = Quill.import('attributors/style/font')
Font.whitelist = ['serif', 'monospace', 'arial', 'times-new-roman', 'courier', 'verdana', 'georgia']
Quill.register(Font, true)

export default {
  name: 'RichTextEditor',
  components: {
    VueEditor
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [String, Array],
      default: ''
    },
    toolbarType: {
      type: String,
      default: 'simple', // 'simple' | 'full'
      validator: (value) => ['simple', 'full'].includes(value)
    }
  },
  computed: {
    customToolbar () {
      if (this.toolbarType === 'full') {
        return [
          ['bold', 'italic', 'underline', 'strike'],
          [{ size: ['8px', '10px', '12px', '14px', false, '18px', '20px', '24px', '32px', '42px', '54px'] }],
          [{ font: ['serif', 'monospace', 'arial', 'times-new-roman', 'courier', 'verdana', 'georgia'] }],
          [{ color: [] }]
        ]
      }
      // Default 'simple' toolbar
      return [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }]
      ]
    }
  },
  data () {
    return {
      internalValue: this.value
    }
  },
  watch: {
    value (newVal) {
      if (newVal !== this.internalValue) {
        this.internalValue = newVal
      }
    }
  },
  methods: {
    handleInput (value) {
      this.$emit('input', value)
    }
  }
}
</script>

<style scoped>
.rich-text-editor {
  margin-bottom: 16px;
}

.rich-text-editor label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}
</style>

<style>
/* Custom font size styles for dropdown */
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before {
  content: '8';
  font-size: 8px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
  content: '10';
  font-size: 10px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before {
  content: '12';
  font-size: 12px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before {
  content: '14';
  font-size: 14px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
  content: '18';
  font-size: 18px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before {
  content: '20';
  font-size: 20px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
  content: '24';
  font-size: 24px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
  content: '32';
  font-size: 32px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="42px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="42px"]::before {
  content: '42';
  font-size: 42px;
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="54px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="54px"]::before {
  content: '54';
  font-size: 54px;
}

/* Custom font styles for Quill editor */
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="serif"]::before {
  content: 'Serif';
  font-family: Georgia, serif;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before {
  content: 'Monospace';
  font-family: 'Courier New', monospace;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before {
  content: 'Arial';
  font-family: Arial, sans-serif;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before {
  content: 'Times New Roman';
  font-family: 'Times New Roman', serif;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier"]::before {
  content: 'Courier';
  font-family: 'Courier New', Courier, monospace;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before {
  content: 'Verdana';
  font-family: Verdana, sans-serif;
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before {
  content: 'Georgia';
  font-family: Georgia, serif;
}

</style>
